/**
 * ProjectReports Component
 *
 * Project-level report suite with reusable floor plan scoping.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Boxes,
  Cable,
  CircleCheck,
  ClipboardList,
  Layers3,
  Loader2,
  Map,
  Package,
  Zap,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { ReportSummaryCard, ReportSummaryGrid } from './primitives/ReportSummaryCard';
import { ReportSection, ReportTable, type ReportTableColumn } from './primitives/ReportTable';
import { WiringReport } from './wiring/WiringReport';
import { useProjectReportData } from '@/hooks/reports/useProjectReportData';
import type {
  DeviceScheduleRow,
  FloorPlanReportRow,
  LoadReportRow,
  ProjectReportFilters,
  QaReportItem,
  RoomReportRow,
} from '@/hooks/reports/useProjectReportData';
import type { ReportType } from '@/types/reports';

interface ProjectReportsProps {
  projectId: string;
  projectName: string;
  clientName?: string;
  currentFloorPlanId?: string;
  currentFloorPlanName?: string;
  defaultFloorPlanId?: string | 'all';
  compactHeader?: boolean;
  canWrite?: boolean;
}

const REPORT_TABS: { id: ReportType; label: string; icon: React.ElementType }[] = [
  { id: 'summary', label: 'Summary', icon: ClipboardList },
  { id: 'devices', label: 'Devices', icon: Package },
  { id: 'loads', label: 'Loads', icon: Zap },
  { id: 'wiring', label: 'Wiring', icon: Cable },
  { id: 'rooms', label: 'Rooms', icon: Map },
  { id: 'qa', label: 'QA', icon: AlertTriangle },
];

function ScopeLabel({
  selectedCount,
  selectedFloorPlanName,
}: {
  selectedCount: number;
  selectedFloorPlanName: string;
}) {
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <Layers3 className="h-4 w-4" />
      <span>{selectedFloorPlanName}</span>
      <Badge variant="secondary">
        {selectedCount} floor plan{selectedCount === 1 ? '' : 's'}
      </Badge>
    </div>
  );
}

function SummaryReport({
  data,
}: {
  data: NonNullable<ReturnType<typeof useProjectReportData>['data']>;
}) {
  return (
    <div className="space-y-6">
      <ReportSummaryGrid className="md:grid-cols-4">
        <ReportSummaryCard label="Floor Plans" value={data.summary.floorPlans} icon={Layers3} />
        <ReportSummaryCard label="Rooms" value={data.summary.rooms} icon={Map} />
        <ReportSummaryCard label="Fixtures" value={data.summary.fixtures} icon={Zap} />
        <ReportSummaryCard label="LV Products" value={data.summary.lvProducts} icon={Package} />
        <ReportSummaryCard label="Controls" value={data.summary.controls} icon={Boxes} />
        <ReportSummaryCard label="LED Strips" value={data.summary.stripLights} icon={Zap} />
        <ReportSummaryCard label="Cable Paths" value={data.summary.cablePaths} icon={Cable} />
        <ReportSummaryCard label="Junctions" value={data.summary.junctions} icon={Boxes} />
      </ReportSummaryGrid>

      <ReportTable
        sectionHeader="Floor Plan Summary"
        data={data.floorPlanRows}
        columns={floorPlanColumns}
        keyExtractor={row => row.id}
        emptyMessage="No floor plans found"
      />
    </div>
  );
}

const floorPlanColumns: ReportTableColumn<FloorPlanReportRow>[] = [
  { key: 'name', header: 'Floor Plan' },
  {
    key: 'scale',
    header: 'Scale',
    render: row => (
      <Badge variant={row.hasScale ? 'secondary' : 'destructive'}>
        {row.hasScale ? 'Set' : 'Missing'}
      </Badge>
    ),
  },
  { key: 'rooms', header: 'Rooms', align: 'right' },
  { key: 'fixtures', header: 'Fixtures', align: 'right' },
  { key: 'stripLights', header: 'Strips', align: 'right' },
  { key: 'controls', header: 'Controls', align: 'right' },
  { key: 'lvProducts', header: 'LV', align: 'right' },
  { key: 'loads', header: 'Loads', align: 'right' },
  { key: 'cablePaths', header: 'Paths', align: 'right' },
  { key: 'junctions', header: 'Boxes', align: 'right' },
  { key: 'elevations', header: 'Elevations', align: 'right' },
];

const deviceColumns: ReportTableColumn<DeviceScheduleRow>[] = [
  { key: 'type', header: 'Type' },
  { key: 'name', header: 'Name' },
  { key: 'category', header: 'Category' },
  { key: 'productName', header: 'Product', hideOnMobile: true },
  { key: 'floorPlanName', header: 'Floor Plan' },
  { key: 'roomName', header: 'Room' },
  { key: 'loadName', header: 'Load' },
  {
    key: 'status',
    header: 'Status',
    render: row => (
      <Badge variant={row.status === 'Ready' ? 'secondary' : 'outline'}>{row.status}</Badge>
    ),
  },
];

function DeviceScheduleReport({
  data,
}: {
  data: NonNullable<ReturnType<typeof useProjectReportData>['data']>;
}) {
  return (
    <ReportTable
      sectionHeader="Device Schedule"
      data={data.deviceRows}
      columns={deviceColumns}
      keyExtractor={row => row.id}
      emptyMessage="No fixtures, controls, or LV products found"
      emptyIcon={<Package className="h-8 w-8" />}
    />
  );
}

const loadColumns: ReportTableColumn<LoadReportRow>[] = [
  { key: 'loadNumber', header: 'Load #', width: '80px' },
  { key: 'name', header: 'Load Name' },
  { key: 'floorPlanName', header: 'Floor Plan' },
  { key: 'fixtureCount', header: 'Fixtures', align: 'right' },
  { key: 'controlCount', header: 'Controls', align: 'right' },
  { key: 'notes', header: 'Notes', hideOnMobile: true },
];

function LoadAssignmentReport({
  data,
}: {
  data: NonNullable<ReturnType<typeof useProjectReportData>['data']>;
}) {
  return (
    <div className="space-y-6">
      <ReportSummaryGrid>
        <ReportSummaryCard label="Total Loads" value={data.summary.loads} icon={Zap} />
        <ReportSummaryCard
          label="Unassigned Fixtures"
          value={data.summary.unassignedFixtures}
          icon={AlertTriangle}
        />
        <ReportSummaryCard
          label="Unassigned Controls"
          value={data.summary.unassignedControls}
          icon={AlertTriangle}
        />
      </ReportSummaryGrid>
      <ReportTable
        sectionHeader="Load Assignment"
        data={data.loadRows}
        columns={loadColumns}
        keyExtractor={row => row.id}
        emptyMessage="No loads found"
        emptyIcon={<Zap className="h-8 w-8" />}
      />
    </div>
  );
}

const roomColumns: ReportTableColumn<RoomReportRow>[] = [
  { key: 'floorPlanName', header: 'Floor Plan' },
  { key: 'roomName', header: 'Room / Zone' },
  { key: 'roomType', header: 'Type' },
  { key: 'fixtures', header: 'Fixtures', align: 'right' },
  { key: 'controls', header: 'Controls', align: 'right' },
  { key: 'lvProducts', header: 'LV Products', align: 'right' },
];

function RoomsReport({
  data,
}: {
  data: NonNullable<ReturnType<typeof useProjectReportData>['data']>;
}) {
  return (
    <ReportTable
      sectionHeader="Rooms / Zones"
      data={data.roomRows}
      columns={roomColumns}
      keyExtractor={row => row.id}
      emptyMessage="No rooms or zones found"
      emptyIcon={<Map className="h-8 w-8" />}
    />
  );
}

function QaReport({
  data,
}: {
  data: NonNullable<ReturnType<typeof useProjectReportData>['data']>;
}) {
  if (data.qaItems.length === 0) {
    return (
      <Card>
        <CardContent className="flex items-center gap-3 p-6">
          <CircleCheck className="h-5 w-5 text-emerald-600" />
          <div>
            <h3 className="font-semibold">No report issues found</h3>
            <p className="text-sm text-muted-foreground">
              The selected floor plan scope has no missing scale, load, or path data flagged by this
              report.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <ReportSection title="QA / Missing Data">
      <div className="space-y-3">
        {data.qaItems.map(item => (
          <QaItemCard key={item.id} item={item} />
        ))}
      </div>
    </ReportSection>
  );
}

function QaItemCard({ item }: { item: QaReportItem }) {
  return (
    <Card>
      <CardContent className="flex items-start justify-between gap-4 p-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Badge variant={item.severity === 'warning' ? 'destructive' : 'secondary'}>
              {item.severity === 'warning' ? 'Warning' : 'Info'}
            </Badge>
            <span className="text-sm font-medium">{item.area}</span>
          </div>
          <p className="text-sm">{item.message}</p>
          <p className="text-xs text-muted-foreground">{item.floorPlanName}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export function ProjectReports({
  projectId,
  projectName,
  clientName,
  currentFloorPlanId,
  currentFloorPlanName,
  defaultFloorPlanId,
  compactHeader = false,
}: ProjectReportsProps) {
  const initialFloorPlanId = defaultFloorPlanId ?? currentFloorPlanId ?? 'all';
  const [activeReport, setActiveReport] = useState<ReportType>('summary');
  const [filters, setFilters] = useState<ProjectReportFilters>({
    floorPlanId: initialFloorPlanId,
  });

  useEffect(() => {
    setFilters({ floorPlanId: initialFloorPlanId });
  }, [initialFloorPlanId]);

  const { data, isLoading, error, refetch } = useProjectReportData(projectId, filters);

  const selectedFloorPlanName = useMemo(() => {
    if (!data)
      return filters.floorPlanId === 'all'
        ? 'All Floor Plans'
        : (currentFloorPlanName ?? 'Current Floor Plan');
    if (filters.floorPlanId === 'all') return 'All Floor Plans';
    return (
      data.floorPlans.find(floorPlan => floorPlan.id === filters.floorPlanId)?.name ??
      'Current Floor Plan'
    );
  }, [currentFloorPlanName, data, filters.floorPlanId]);

  const handleFloorPlanChange = useCallback((floorPlanId: string) => {
    setFilters({ floorPlanId: floorPlanId as string | 'all' });
  }, []);

  const floorPlans = data?.floorPlans ?? [];
  const selectedCount =
    data?.selectedFloorPlans.length ?? (filters.floorPlanId === 'all' ? floorPlans.length : 1);

  return (
    <div className="h-full flex flex-col gap-6 p-6">
      <div className="flex flex-col gap-4 print:hidden">
        {!compactHeader && (
          <div>
            <h2 className="text-2xl font-semibold">Reports</h2>
            <p className="text-sm text-muted-foreground">
              Project schedules, loads, wiring, rooms, and QA checks.
            </p>
          </div>
        )}

        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <ScopeLabel selectedCount={selectedCount} selectedFloorPlanName={selectedFloorPlanName} />
          <Select value={filters.floorPlanId} onValueChange={handleFloorPlanChange}>
            <SelectTrigger className="w-full lg:w-[260px]">
              <SelectValue placeholder="Select report scope" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Floor Plans</SelectItem>
              {floorPlans.map(floorPlan => (
                <SelectItem key={floorPlan.id} value={floorPlan.id}>
                  {floorPlan.name}
                  {floorPlan.id === currentFloorPlanId ? ' (current)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs value={activeReport} onValueChange={value => setActiveReport(value as ReportType)}>
        <TabsList className="h-auto flex flex-wrap justify-start gap-1 print:hidden">
          {REPORT_TABS.map(report => {
            const Icon = report.icon;
            return (
              <TabsTrigger key={report.id} value={report.id} className="gap-2">
                <Icon className="h-4 w-4" />
                {report.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-3 text-muted-foreground">Loading report data...</span>
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center justify-center py-20">
            <p className="font-medium text-destructive">Failed to load report data</p>
            <p className="text-sm text-muted-foreground mt-1">
              Something went wrong while loading this report. Please try again.
            </p>
            <Button variant="outline" className="mt-4" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        )}

        {data && !isLoading && !error && (
          <div className="mt-6">
            <TabsContent value="summary">
              <SummaryReport data={data} />
            </TabsContent>
            <TabsContent value="devices">
              <DeviceScheduleReport data={data} />
            </TabsContent>
            <TabsContent value="loads">
              <LoadAssignmentReport data={data} />
            </TabsContent>
            <TabsContent value="wiring">
              <WiringReport
                key={filters.floorPlanId}
                projectId={projectId}
                projectName={projectName}
                clientName={clientName}
                initialFloorPlanId={filters.floorPlanId}
              />
            </TabsContent>
            <TabsContent value="rooms">
              <RoomsReport data={data} />
            </TabsContent>
            <TabsContent value="qa">
              <QaReport data={data} />
            </TabsContent>
          </div>
        )}
      </Tabs>
    </div>
  );
}
